import { useContext } from 'react';

import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { TabNavSettingContext } from 'contexts/TabNavSettingContext';
import { isTabNavEnabledByEnv } from 'utils/tabNavEnv';

/**
 * Single source of truth for whether the In-App Tab Bar is active for this user, right now.
 *
 * Three layers decide it, the first match wins:
 *   1. REACT_APP_TAB_NAV — build-time kill switch, so the feature can be pulled in an emergency
 *      without touching the database or waiting for any tenant admin.
 *   2. The user's own choice, kept per browser.
 *   3. The company default, delivered on the current user payload from the API.
 *
 * The last fallback matters for tenants whose Setting row has not been migrated yet: rather than
 * leaving them in an undefined state, they get the feature.
 */

const useTabNavActive = (): boolean => {
  const { currentUser } = useContext(CurrentUserContext);
  const { preference } = useContext(TabNavSettingContext);

  if (!isTabNavEnabledByEnv()) {
    return false;
  }

  if (preference === 'on') {
    return true;
  }

  if (preference === 'off') {
    return false;
  }

  return currentUser && typeof currentUser.inAppTabNav === 'boolean' ? currentUser.inAppTabNav : true;
};

export default useTabNavActive;
